import { useAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import { scenarioFiltersAtomFamily } from '../state/hazard.atoms'
import {
  ActiveScenarioFilters,
  buildFiltersFromScenario,
  filterHazardScenarios,
} from '../utils/hazardScenarioFilters.utils'
import { HazardScenario } from '../types/hazard.types'

type UseHazardScenarioFiltersParams = {
  layerId: string
  filteredScenarios: HazardScenario[]
  filterableParams: string[]
  selectedAssetTag: string | null
  onSelectScenario: (assetTag: string | null) => void
}

type UseHazardScenarioFiltersReturn = {
  activeFilters: ActiveScenarioFilters
  setActiveFilters: (filters: ActiveScenarioFilters) => void
  handleSelectScenario: (assetTag: string) => void
  handleResetFilters: () => void
}

/**
 * Manages hazard scenario filter state for a given layer.
 *
 * Responsibilities:
 * - Owns activeFilters via scenarioFiltersAtomFamily (scoped per layerId)
 * - Initialises filters from the active scenario on first load
 * - Clears selected scenario if it's filtered out
 * - Syncs filters when a new scenario is selected
 * - Resets filters back to the selected scenario's parameters (or clears if none)
 */
export const useHazardScenarioFilters = ({
  layerId,
  filteredScenarios,
  filterableParams,
  selectedAssetTag,
  onSelectScenario,
}: UseHazardScenarioFiltersParams): UseHazardScenarioFiltersReturn => {
  const [activeFilters, setActiveFilters] = useAtom(scenarioFiltersAtomFamily(layerId))
  const hasInitialised = useRef(false)

  // filters to match the active scenario on first load
  useEffect(() => {
    if (hasInitialised.current) return
    if (!selectedAssetTag || filteredScenarios.length === 0 || filterableParams.length === 0) return

    const scenario = filteredScenarios.find((s) => s.assetTag === selectedAssetTag)
    if (!scenario) return

    setActiveFilters(buildFiltersFromScenario(scenario, filterableParams))
    hasInitialised.current = true
  }, [filteredScenarios, filterableParams, selectedAssetTag, setActiveFilters])

  // clear selected scenario if it's no longer visible after filtering
  useEffect(() => {
    if (!selectedAssetTag) return
    const displayScenarios = filterHazardScenarios(filteredScenarios, activeFilters)
    const isStillVisible = displayScenarios.some((s) => s.assetTag === selectedAssetTag)
    if (!isStillVisible) onSelectScenario(null)
  }, [activeFilters, filteredScenarios, selectedAssetTag, onSelectScenario])

  const handleSelectScenario = (assetTag: string) => {
    onSelectScenario(assetTag)
    const scenario = filteredScenarios.find((s) => s.assetTag === assetTag)
    if (!scenario) return
    setActiveFilters(buildFiltersFromScenario(scenario, filterableParams))
  }

  const handleResetFilters = () => {
    if (!selectedAssetTag) {
      setActiveFilters({})
      return
    }
    const scenario = filteredScenarios.find((s) => s.assetTag === selectedAssetTag)
    setActiveFilters(scenario ? buildFiltersFromScenario(scenario, filterableParams) : {})
  }

  return {
    activeFilters,
    setActiveFilters,
    handleSelectScenario,
    handleResetFilters,
  }
}
